import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { AuthLayout } from "./AuthLayout";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "could not reset the password";
}

/**
 * The public screen a reset link lands on. Unlike AcceptInvite, this does
 * NOT sign the caller in afterward — plan 11 §4's "reset revokes sessions"
 * reads as a recovery action, not a login, so the ordinary flow is "your
 * password was changed, now sign in."
 */
export function ResetPassword({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <div className="card signin">
          <h1>Password changed</h1>
          <p className="shell__subtitle">Every existing session was signed out.</p>
          <a href="/">Go to sign in</a>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className="card signin" onSubmit={(event) => void handleSubmit(event)}>
        <h1>Choose a new password</h1>
        <label className="signin__field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={12}
            autoFocus
          />
        </label>
        <label className="signin__field">
          <span>Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
            minLength={12}
          />
        </label>
        {error ? (
          <p className="card__error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" disabled={submitting}>
          {submitting ? "Changing password…" : "Change password"}
        </button>
      </form>
    </AuthLayout>
  );
}
